﻿export class ObjectUtils {

  static hasProperties(obj) {
    const hasOwnProperty = Object.prototype.hasOwnProperty;
    for (var prop in obj) {
      if (hasOwnProperty.call(obj, prop)) {
        return true;
      }
    }
    return false;
  }

  /* -----------------------------------------------------------------------------------------
   * https://raw.githubusercontent.com/ReactiveSets/toubkal/master/lib/util/value_equals.js
     equalContents( a, b [, enforce_properties_order, cyclic] )

     Returns true if a and b are deeply equal, false otherwise.

     Parameters:
       - a (Any type): value to compare to b
       - b (Any type): value compared to a

     Optional Parameters:
       - enforce_properties_order (Boolean): true to check if Object properties are provided
         in the same order between a and b

       - cyclic (Boolean): true to check for cycles in cyclic objects

     Implementation:
       'a' is considered equal to 'b' if all scalar values in a and b are strictly equal as
       compared with operator '===' except for these two special cases:
         - 0 === -0 but are not equal.
         - NaN is not === to itself but is equal.

       RegExp objects are considered equal if they have the same lastIndex, i.e. both regular
       expressions have matched the same number of times.

       Functions must be identical, so that they have the same closure context.

       "undefined" is a valid value, including in Objects

       106 automated tests.

       Provide options for slower, less-common use cases:

         - Unless enforce_properties_order is true, if 'a' and 'b' are non-Array Objects, the
           order of occurence of their attributes is considered irrelevant:
             { a: 1, b: 2 } is considered equal to { b: 2, a: 1 }

         - Unless cyclic is true, Cyclic objects will throw:
             RangeError: Maximum call stack size exceeded
  */
  static equalContents(a, b, enforce_properties_order, cyclic) {
    var toString = Object.prototype.toString;

    return a === b       // strick equality should be enough unless zero
      && a !== 0         // because 0 === -0, requires test by _equals()
      || _equals(a, b);  // handles not strictly equal or zero values

    function _equals(a, b) {
      // a and b have already failed test for strict equality or are zero

      var s, l, p, x, y;

      // They should have the same toString() signature
      if ((s = toString.call(a)) !== toString.call(b)) return false;

      switch (s) {
        default: // Boolean, Date, String
          return a.valueOf() === b.valueOf();


        case '[object Number]':
          // Converts Number instances into primitive values
          // This is required also for NaN test bellow
          a = +a;
          b = +b;

          return a ?         // a is Non-zero and Non-NaN
            a === b
            :                // a is 0, -0 or NaN
            a === a ?       // a is 0 or -O
              1 / a === 1 / b    // 1/0 !== 1/-0 because Infinity !== -Infinity
              : b !== b;         // NaN, the only Number not equal to itself!


        case '[object RegExp]':
          return a.source == b.source
            && a.global == b.global
            && a.ignoreCase == b.ignoreCase
            && a.multiline == b.multiline
            && a.lastIndex == b.lastIndex;


        case '[object Function]':
          return false; // functions should be strictly equal because of closure context


        case '[object Array]':
          if (cyclic && (x = reference_equals(a, b)) !== null) return x; // intentionally duplicated bellow for [object Object]

          if ((l = a.length) != b.length) return false;
          // Both have as many elements

          while (l--) {
            if ((x = a[l]) === (y = b[l]) && x !== 0 || _equals(x, y)) continue;

            return false;
          }

          return true;


        case '[object Object]':
          if (cyclic && (x = reference_equals(a, b)) !== null) return x; // intentionally duplicated from above for [object Array]

          l = 0; // counter of own properties

          if (enforce_properties_order) {
            var properties = [];

            for (p in a) {
              if (a.hasOwnProperty(p)) {
                properties.push(p);

                if ((x = a[p]) === (y = b[p]) && x !== 0 || _equals(x, y)) continue;

                return false;
              }
            }

            // Check if 'b' has as the same properties as 'a' in the same order
            for (p in b)
              if (b.hasOwnProperty(p) && properties[l++] != p)
                return false;
          } else {
            for (p in a) {
              if (a.hasOwnProperty(p)) {
                ++l;

                if ((x = a[p]) === (y = b[p]) && x !== 0 || _equals(x, y)) continue;

                return false;
              }
            }

            // Check if 'b' has as not more own properties than 'a'
            for (p in b)
              if (b.hasOwnProperty(p) && --l < 0)
                return false;
          }

          return true;
      } // switch toString.call( a )
    } // _equals()

    /* -----------------------------------------------------------------------------------------
       reference_equals( a, b )

       Helper function to compare object references on cyclic objects or arrays.

       Returns:
         - null if a or b is not part of a cycle, adding them to object_references array
         - true: same cycle found for a and b
         - false: different cycle found for a and b

       On the first call of a specific invocation of equal(), replaces self with inner function
       holding object_references array object in closure context.

       This allows to create a context only if and when an invocation of equal() compares
       objects or arrays.
    */
    /*eslint no-func-assign: "off"*/
    function reference_equals(a, b) {
      var object_references = [];

      return (reference_equals = _reference_equals)(a, b);

      function _reference_equals(a, b) {
        var l = object_references.length;

        while (l--)
          if (object_references[l--] === b)
            return object_references[l] === a;

        object_references.push(a, b);

        return null;
      } // _reference_equals()
    } // reference_equals()
  } // equalContents()

  static clone(source, deepClone) {
    if (!source) {
      return undefined;
    }
    if (source === null) {
      return null;
    }
    if (Array.isArray(source)) {
      const dest = Array(source.length);
      let i = 0;
      source.forEach(item => {
        if (deepClone && item !== null && typeof (item) === 'object')
          dest[i] = ObjectUtils.clone(item, deepClone);
        else
          dest[i] = item;
        i++;
      });
      return dest;
    } else {
      return ObjectUtils.assign({}, source, deepClone);
    }
  }

  static isAssigned(obj) {
    return typeof obj !== 'undefined' && obj !== null;
  }

  static assign(dest, source, deepClone) {
    for (var i in source) {
      if (deepClone && source[i] !== null && typeof (source[i]) === 'object')
        dest[i] = ObjectUtils.clone(source[i], deepClone);
      else
        dest[i] = source[i];
    }
    return dest;
  }

  // find nearest ancestor element causing the predicate callback to return true
  static findAncestor(element, predicate) {
    return element && (predicate(element) ? element : this.findAncestor(element.parentNode, predicate));
  }

  static clearChildren(element) {
    while (element.firstChild) {
      element.removeChild(element.firstChild);
    }
  }

  static getScrollParent(node) {
    if (node === window) {
      return null;
    }
    const isElement = node instanceof HTMLElement;
    const overflowY = isElement && window.getComputedStyle(node).overflowY;
    const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden';
    if (!node) {
      return null;
    } else if (isScrollable && node.scrollHeight >= node.clientHeight) {
      return node;
    }
    return this.getScrollParent(node.parentNode) || document.body;
  }

  /* Not correctly implemented, but also not needed at this point */
  //static isScrolledIntoView(el, mustbeFullyVisible) {
  //  let isVisible = false;
  //  const scrollParent = this.getScrollParent(el);
  //  if (scrollParent) {
  //    const rect = el.getBoundingClientRect();
  //    const elemTop = rect.top;
  //    const elemBottom = rect.bottom;
  //    const innerHeight = scrollParent === window ? window.innerHeight : scrollParent.clientHeight;
  //    if (mustbeFullyVisible) {
  //      // Only completely visible elements return true:
  //      isVisible = (elemTop >= 0) && (elemBottom <= innerHeight);
  //    } else {
  //      // Partially visible elements return true:
  //      isVisible = elemTop < innerHeight && elemBottom >= 0;
  //    }
  //  }
  //  return isVisible;
  //}

  static isNullOrEmpty(text) {
    return typeof text === 'undefined' || text === null || text === '';
  }

  // Uses "away from zero" rounding (the Math.round uses "Banker's rounding").
  static round(value) {
    return value < 0 ? -Math.round(-value) : Math.round(value);
  }

  /**
   * Rounds to neareast decimals, is fast, but is flawed due to floating point rounding inaccuracies.
   * roundDecInAcc(1.005, 2) might return 1.00 instead of 1.01.
   */
  static roundDecInAcc(number, decimals) {
    const factorOfTen = Math.pow(10, decimals);
    return Math.round(number * factorOfTen) / factorOfTen;
  }

  /**
   * Rounds to neareast decimals, is not as fast due to string conversion, but handles floating point
   * rounding inaccuracies. roundDec(1.005, 2) always returns 1.01.
   */
  static roundDec(number, decimals) {
    const tmp = Math.round(number + 'e' + decimals);
    return Number(tmp + 'e-' + decimals);
  }

  static removeFromArray(array, item) {
    const pos = array.indexOf(item);
    if (pos > -1) {
      return array.splice(pos, 1);
    }
    return null;
  }

  static async sleep(ms) {
    return new Promise(resolve => {
      setTimeout(resolve, ms);
    });
  }
}

/**
 * Allows one part of the code to wait for another part of the code to complete an asynchronous
 * operation. Wrap the async code with calls to clear() and set(), and call "await hasBeenSet()"
 * in the code which needs to wait. */
export class PromiseFlag {
  constructor() {
  }

  clear() {
    this.promise = new Promise(resolve => {
      this.resolve = resolve;
    });
  }

  set() {
    if (this.promise) {
      this.resolve();
      this.promise = null;
      this.resolve = null;
    }
  }

  async hasBeenSet() {
    if (this.promise) {
      await this.promise;
    }
  }
}

export class StringUtils {
  static isNullOrEmpty(value) {
    // Note, value == null catches both null and undefined
    return (value == null || value === '');
  }

  static isNullOrWhiteSpace(value) {
    /* We're taking advantage of the fact that in JS
     * 1) both null and undefined == null, and
     * 2) a string with only whitespace equalsy 0, but
     * 3) we have test for 0 and '0' since they also equalsy 0, but they are not whitespace
     *    (in our definition). */
    return value == null || (value == 0 && value !== 0 && value !== '0');
  }

  /* Joins the left and right strings with separator. If either (or both) are null or empty,
   * then the separator is not used. */
  static join(separator, left, right, trimResult /*= true*/) {
    const leftEmpty = StringUtils.isNullOrEmpty(left);
    const rightEmpty = StringUtils.isNullOrEmpty(right);
    if (leftEmpty && rightEmpty)
      return null;

    let result;
    if (leftEmpty)
      result = right;
    else if (rightEmpty)
      result = left;
    else
      result = left + separator + right;
    return trimResult ? result.Trim() : result;
  }

  static insert(str, index, insertedStr) {
    return str.slice(0, index) + insertedStr + str.slice(index);
  }

  static truncateString(str, len, truncateWholeWords) {
    if (len <= 0) {
      return null;
    }
    if (str === null || len >= str.length) {
      return str;
    }
    if (!truncateWholeWords || (len < str.length - 1 && str[len] === ' ')) {
      return str.slice(0, len - 1);
    }
    let ndx = len;
    while (ndx > 0 && str[ndx] !== ' ') {
      ndx--;
    }
    return ndx > 0 ? str.slice(0, ndx - 1) : str.slice(0, len - 1);
  }

  static removeChars(str, chars) {
    if (str === null || chars === null || chars === '')
      return str;
    var arr = str.split('');
    const filteredArr = arr.filter(ch => chars.indexOf(ch) === -1);
    return filteredArr.join('');
  }

  static addTrailingSlash(str) {
    if (!str || str === null || str === '') {
      return '/';
    }
    if (!str.endsWith('/')) {
      return str + '/';
    }
    return str;
  }

  static replaceCharAt(str, index, chr) {
    if (index > str.length - 1) return str;
    return str.substring(0, index) + chr + str.substring(index + 1);
  }

  static isJson(str, canBeObject = true, canBeArray = true, canBeString = false, canBeNumber = false) {
    try {
      const json = JSON.parse(str);
      if (!json) {
        return false;
      }
      const type = typeof json;
      if (!canBeObject && type === 'object') {
        return false;
      }
      if (!canBeArray && Array.isArray(json)) {
        return false;
      }
      if (!canBeString && type === 'string') {
        return false;
      }
      if (!canBeNumber && type === 'number' && !isNaN(json)) {
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  }
}

/** Calls the onComplete callback when both minDuration ms. has elapsed and the complete() method
  * has been called. Use this class if you want to call some code, but not until at least x ms
  * have elapsed. */
export class MinDuration {
  constructor(minDuration, onComplete) {
    this.minDuration = minDuration;
    this.onComplete = onComplete;
    this.timeout = setTimeout(() => this.timeoutExpired(), this.minDuration);
  }

  completeIfReady() {
    if (this.isCompleted && this.isTimeoutExpired && this.onComplete) {
      this.onComplete(this);
    }
  }

  timeoutExpired() {
    this.isTimeoutExpired = true;
    this.completeIfReady();
  }

  complete() {
    this.isCompleted = true;
    this.completeIfReady();
  }
}

export class Utils {
  /** Will start a loop calling requestAnimationFrame until either the "until": callback returns true,
   * or ms milliseconds elapses. If "until" returns true, then the onSuccess is called. Otherwise, the
   * onTimeout is called. */
  static requestAnimationFramesUntil(ms, until, onSuccess, onTimedOut) {
    const startTime = Date.now();
    const onFrame = () => {
      if (until()) {
        onSuccess();
      } else if (Date.now() - startTime < ms) {
        //console.log('Requesting another frame...');
        requestAnimationFrame(onFrame);
      } else {
        //console.log('Timed out requesting frames!');
        if (onTimedOut) {
          onTimedOut();
        }
      }
    };
    requestAnimationFrame(onFrame);
  }

  static addEventListener(element, name, eventHandler, options = true) {
    element.addEventListener(name, eventHandler, options);
    return { element, name, eventHandler, options };
  }

  static removeEventListener(listener) {
    if (listener) {
      listener.element.removeEventListener(listener.name, listener.eventHandler, listener.options);
    }
  }

  static cssColorStr(str) {
    const ctx = document.createElement('canvas').getContext('2d');
    ctx.fillStyle = str;
    return ctx.fillStyle;
  }

  static cssColor(str) {
    let colorStr = GraphicsHelper.cssColorStr(str);
    colorStr = colorStr.slice(1);
    const color = parseInt('0x' + colorStr);
    return color;
  }

  /**
   * Returns a new array with the values found in both array1 and array2.
   */
  static getArrayIntersect(array1, array2) {
    if (!array1 || array1.length === 0) return array2;
    if (!array2 || array2.length === 0) return array1;
    const result = [];
    array1.forEach(value => {
      if (array2.indexOf(value) > -1) {
        result.push(value);
      }
    })
    return result;
  }
}

export class RepeatWhileMouseDown {
  constructor(element, interval, onMouseDown, onRepeat, onMouseUp) {
    this.interval = interval;
    this.initialDelay = 300; // ms
    this.onMouseDown = onMouseDown;
    this.onRepeat = onRepeat;
    this.onMouseUp = onMouseUp;
    element.addEventListener('mousedown', () => this.handleMouseDown());
    element.addEventListener('mouseup', () => this.handleMouseUp());
    // Use native events for touch devices:
    element.addEventListener('touchstart', ev => this.handleTouchStart(ev), { passive: false });
    element.addEventListener('touchend', ev => this.handleTouchEnd(ev));
  }

  handleMouseDown() {
    //console.log('mousedown');
    this.onMouseDown();
    this.delayHandle = setTimeout(() => this.handleMouseDownDelay(), this.initialDelay);
  }

  handleMouseUp() {
    //console.log('mouseup');
    if (this.autoRepeating) {
      this.autoRepeating = false;
      clearInterval(this.intervalHandle);
      this.onMouseUp(false);
    } else {
      clearTimeout(this.delayHandle);
      this.onMouseUp(true);
    }
  }

  handleMouseDownDelay() {
    //console.log('repeating...');
    this.autoRepeating = true;
    this.intervalHandle = setInterval(() => this.onRepeat(), this.interval);
  }

  /**
   * Is only fired on touch devices.
   */
  handleTouchStart(ev) {
    //console.log('touchstart');
    ev.preventDefault(); // Block the mousedown event, we don't need it
    this.onMouseDown();
    // The typical timer interval (this.interval) is too long for touch devices
    this.interval = this.interval / 2;
    this.delayHandle = setTimeout(() => this.handleMouseDownDelay(), this.initialDelay);
  }

  /**
   * Is only fired on touch devices.
   */
  handleTouchEnd(ev) {
    //console.log('touchend');
    // Use same code as for mouse up event:
    this.handleMouseUp();
  }
}

/**
 * dragToScroll enables pointer devices to scroll an element by using a drag operation. On touch
 * devices this functionality is built-in.
 */
export class DragToScroll {
  /**
   * DragToScroll constructor.
   * @param {any} childElement A child element isn't required, but when provided, drag operations
   * will only work on the child element or it's children. Drag operations on the parent element
   * (outside the bounds of the child element) will be ignored.
   */
  constructor(parentElement, childElement, autoStart) {
    this.parentElement = parentElement;
    this.origScrollTop = 0;
    this.origScrollLeft = 0;
    this.origMouseX = 0;
    this.origMouseY = 0;
    this.updateChildElement(this.childElement, true);
    if (autoStart) {
      this.start();
    }
  }

  start() {
    if (!this.isActive) {
      if (this.dragElement) {
        this.mouseDownListener = Utils.addEventListener(this.dragElement, 'mousedown', (e) => this.handleMouseDown(e));
      }
      this.isActive = true;
      //console.log('Started');
    }
  }

  stop() {
    if (this.isActive) {
      this.isActive = false;
      //console.log('Stopped');
      Utils.removeEventListener(this.mouseDownListener);
      Utils.removeEventListener(this.mouseUpListener);
      Utils.removeEventListener(this.mouseMoveListener);
    }
  }

  updateChildElement(childElement, forceSetter) {
    if (forceSetter || childElement !== this.childElement) {
      if (this.isActive && this.mouseDownListener) {
        Utils.removeEventListener(this.mouseDownListener);
      }
      this.childElement = childElement;
      this.dragElement = this.childElement || this.parentElement;
      if (this.isActive && this.dragElement) {
        this.mouseDownListener = Utils.addEventListener(this.dragElement, 'mousedown', (e) => this.handleMouseDown(e));
      }
    }
  }

  handleMouseDown(e) {
    //console.log('mousedown');
    this.mouseUpListener = Utils.addEventListener(window, 'mouseup', (e) => this.handleMouseUp(e));
    this.mouseMoveListener = Utils.addEventListener(this.dragElement, 'mousemove', (e) => this.handleMouseMove(e));

    // Save the current scroll position:
    this.origScrollLeft = this.parentElement.scrollLeft;
    this.origScrollTop = this.parentElement.scrollTop;
    // Save the current mouse position:
    this.origMouseX = e.clientX;
    this.origMouseY = e.clientY;

    this.childElement.style.userSelect = 'none'; // Prevent any drag operation from selecting text
    if (this.childElement) {
      this.oldCursor = this.childElement.style.cursor;
      this.childElement.style.cursor = 'grabbing';
    }
    this.isDragging = true;
  }

  handleMouseUp(e) {
    //console.log('mouseup');
    this.isDragging = false;
    Utils.removeEventListener(this.mouseUpListener);
    Utils.removeEventListener(this.mouseMoveListener);
    this.childElement.style.removeProperty('user-select'); // Undo the userSelect style previously set
    if (this.childElement && typeof this.oldCursor !== 'undefined') {
      this.childElement.style.cursor = this.oldCursor;
    }
  }

  handleMouseMove(e) {
    //console.log('mousemove');
    // How far the mouse has been moved:
    const dx = e.clientX - this.origMouseX;
    const dy = e.clientY - this.origMouseY;

    // Scroll the element
    this.parentElement.scrollTop = this.origScrollTop - dy;
    this.parentElement.scrollLeft = this.origScrollLeft - dx;
  }
}

/**
 * Creates a QueryStringObject.query property which is an object where all property names are lowercase.
 * Each property is an item in the query string passed to the constructor. If the query string is
 * undefined, null, or '', then window.location.search is used instead. Note, this class does not
 * support duplicate query string keys.
 */
export class QueryStringAsObject {
  constructor(queryString) {
    queryString = queryString || window.location.search;
    this.parse(queryString);
  }

  parse(queryString) {
    let query = {};
    if (queryString) {
      const pairs = (queryString[0] === '?' ? queryString.substr(1) : queryString).split('&');
      for (var i = 0; i < pairs.length; i++) {
        const pair = pairs[i].split('=');
        let key = decodeURIComponent(pair[0]).toLowerCase();
        query[key] = decodeURIComponent(pair[1] || '');
      }
    }
    return this.query = query;
  }

  parseFullUrl(url) {
    if (url) {
      const index = url.indexOf('?');
      if (index !== -1) {
        return this.parse(url.substring(index))
      }
    }
    return this.parse(null);
  }
}

export class UrlUtils {

  static createQueryString(queryStringParams, ignoreNullValues) {
    // First, filter out undefined values:
    const paramMatrix = Object.entries(queryStringParams); // A matrix where column 1 has each param key, and column 2 each param value
    const callback = (accParams, [key, value]) => {
      if (value !== undefined && (!ignoreNullValues || value !== null)) {
        accParams[key] = value;
      }
      return accParams;
    };
    const definedSearchParams = paramMatrix.reduce(callback, {});

    // Then construct the query string:
    return new URLSearchParams(definedSearchParams).toString();
  }

  static createUrlWithQueryString(url, queryStringParams, ignoreNullValues) {
    const queryString = this.createQueryString(queryStringParams, ignoreNullValues);
    return `${url}?${queryString}`;
  }
}

export class AsyncUtils {
  static waitAsync(ms, arg) {
    return new Promise(resolve => setTimeout(resolve, ms, arg));
  }
}